/**
 * rendi-bottom-sheet — bottom sheet riutilizzabile del wizard.
 *
 * Guscio condiviso da ogni drawer del form: scrim, sheet, grabber con
 * swipe-to-close, chiusura su overlay/bottone/Escape, scroll-lock del body e
 * focus-trap autonomo. I trigger vivono nella form e portano
 * data-sheet-open="<id>"; risponde lo sheet con data-sheet="<id>".
 *
 * Pannelli opzionali: un trigger può portare data-sheet-panel="<key>" per aprire
 * lo sheet su un pannello specifico. In quel caso lo sheet mostra il
 * [data-sheet-panel="<key>"] corrispondente, nasconde gli altri e, se il pannello
 * porta data-sheet-panel-title, lo usa come titolo (il drawer brand). Senza
 * pannelli lo sheet apre con il suo contenuto e titolo statici (il drawer Plus).
 */
if (!customElements.get('rendi-bottom-sheet')) {
  const CLOSE_DURATION = 360;
  const SWIPE_CLOSE_THRESHOLD = 110;
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([type="hidden"]):not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class RendiBottomSheet extends HTMLElement {
    connectedCallback() {
      this.sheetId = this.dataset.sheet || '';
      this.overlay = this.querySelector('[data-sheet-overlay]');
      this.surface = this.querySelector('[data-sheet-surface]');
      this.titleNode = this.querySelector('[data-sheet-title]');
      // every [data-sheet-close] closes: the head's X but also any CTA in the
      // body (e.g. "Ho capito" in the cart sheets); the first one stays the
      // focus target on open
      this.closeButtons = Array.from(this.querySelectorAll('[data-sheet-close]'));
      this.closeButton = this.closeButtons[0];
      this.grabber = this.querySelector('[data-sheet-grabber]');
      this.panels = Array.from(this.querySelectorAll('[data-sheet-panel]'));

      this.lastFocused = null;
      this.hideTimer = null;
      this.dragging = false;

      this.handleTriggerBound = this.handleTrigger.bind(this);
      this.handleKeydownBound = this.handleKeydown.bind(this);
      this.handleFocusinBound = this.handleFocusin.bind(this);
      this.closeBound = this.close.bind(this);
      this.onPointerDownBound = this.onPointerDown.bind(this);
      this.onPointerMoveBound = this.onPointerMove.bind(this);
      this.onPointerUpBound = this.onPointerUp.bind(this);

      this.triggerRoot = this.closest('rendi-sell-form') || document;
      this.triggerRoot.addEventListener('click', this.handleTriggerBound);
      this.overlay?.addEventListener('click', this.closeBound);
      this.closeButtons.forEach((btn) => btn.addEventListener('click', this.closeBound));
      this.grabber?.addEventListener('pointerdown', this.onPointerDownBound);
      // Close when the cart drawer opens; close() is a no-op if not open.
      document.addEventListener('on:cart-drawer:before-open', this.closeBound);
    }

    disconnectedCallback() {
      this.triggerRoot?.removeEventListener('click', this.handleTriggerBound);
      this.overlay?.removeEventListener('click', this.closeBound);
      this.closeButtons?.forEach((btn) => btn.removeEventListener('click', this.closeBound));
      this.grabber?.removeEventListener('pointerdown', this.onPointerDownBound);
      document.removeEventListener('on:cart-drawer:before-open', this.closeBound);
      document.removeEventListener('keydown', this.handleKeydownBound);
      document.removeEventListener('focusin', this.handleFocusinBound);
      this.restoreFocus();
      this.lockScroll(false);
      document.body.classList.remove('rendi-sheet-open');
      if (this.hideTimer) clearTimeout(this.hideTimer);
    }

    // Più sheet condividono il triggerRoot: ognuno reagisce solo ai trigger
    // marcati con il proprio id (data-sheet-open === data-sheet).
    handleTrigger(event) {
      const trigger = event.target.closest('[data-sheet-open]');
      if (!trigger || trigger.dataset.sheetOpen !== this.sheetId) return;
      if (!this.triggerRoot.contains(trigger)) return;
      event.preventDefault();
      this.lastFocused = trigger;
      this.open(trigger.dataset.sheetPanel);
    }

    open(panelKey) {
      const wasHidden = this.hidden;

      // Con i pannelli mostra solo quello richiesto e prendi da lì il titolo.
      // Senza pannelli lo sheet conserva il suo titolo e contenuto statici.
      if (this.panels.length) {
        let title = '';
        this.panels.forEach((panel) => {
          const matches = panel.dataset.sheetPanel === panelKey;
          panel.hidden = !matches;
          if (matches) title = panel.dataset.sheetPanelTitle || '';
        });
        if (this.titleNode && title) this.titleNode.textContent = title;
      }

      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }

      this.hidden = false;
      // Cattura lo scroll della pagina solo su un'apertura vera, così chiamate
      // rientranti (cambio pannello a sheet già aperto) non lo sovrascrivono.
      if (wasHidden) {
        this.lockScroll(true);
        // segnala che uno sheet è aperto, così la sticky bar si alza sopra
        document.body.classList.add('rendi-sheet-open');
      }
      document.addEventListener('keydown', this.handleKeydownBound);
      document.addEventListener('focusin', this.handleFocusinBound);

      // Frame successivo così la transizione parte dallo stato nascosto.
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        (this.closeButton || this.surface)?.focus();
      });
    }

    close() {
      if (this.hidden) return;
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.handleKeydownBound);
      document.removeEventListener('focusin', this.handleFocusinBound);
      this.lockScroll(false);
      document.body.classList.remove('rendi-sheet-open');

      this.hideTimer = setTimeout(() => {
        this.hidden = true;
        this.hideTimer = null;
      }, CLOSE_DURATION);

      this.restoreFocus();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab' || !this.surface) return;

      const focusable = Array.from(this.surface.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((element) => !element.hidden && element.getClientRects().length > 0);

      if (focusable.length === 0) {
        event.preventDefault();
        this.surface.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.surface)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleFocusin(event) {
      if (!this.surface?.contains(event.target)) {
        (this.closeButton || this.surface)?.focus();
      }
    }

    restoreFocus() {
      if (this.lastFocused?.isConnected) {
        this.lastFocused.focus();
      }
      this.lastFocused = null;
    }

    // ---- Swipe-down to close ----
    onPointerDown(event) {
      this.dragging = true;
      this.startY = event.clientY;
      this.curY = event.clientY;
      this.classList.add('is-dragging');
      this.grabber.setPointerCapture?.(event.pointerId);
      this.grabber.addEventListener('pointermove', this.onPointerMoveBound);
      this.grabber.addEventListener('pointerup', this.onPointerUpBound);
      this.grabber.addEventListener('pointercancel', this.onPointerUpBound);
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      this.curY = event.clientY;
      const dy = Math.max(0, this.curY - this.startY);
      this.surface.style.transform = `translateY(${dy}px)`;
      if (this.overlay) this.overlay.style.opacity = String(Math.max(0, 1 - dy / 400));
    }

    onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      this.classList.remove('is-dragging');
      this.grabber.removeEventListener('pointermove', this.onPointerMoveBound);
      this.grabber.removeEventListener('pointerup', this.onPointerUpBound);
      this.grabber.removeEventListener('pointercancel', this.onPointerUpBound);

      const dy = this.curY - this.startY;
      this.surface.style.transform = '';
      if (this.overlay) this.overlay.style.opacity = '';
      if (dy > SWIPE_CLOSE_THRESHOLD) this.close();
    }

    lockScroll(locked) {
      if (locked) {
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      } else {
        if (this.previousOverflow !== undefined) {
          document.body.style.overflow = this.previousOverflow;
          this.previousOverflow = undefined;
        }
      }
    }
  }

  customElements.define('rendi-bottom-sheet', RendiBottomSheet);
}
